import React from 'react';
import '../styles/Landing.css';
import '../styles/About.css';
import Logo from '../components/Logo';
import {
  IconCompass,
  IconTag,
  IconBolt,
} from '../components/MinimalIcons';
import { IconSpark, IconShield, IconCollab } from '../components/Icons';
import usePageMeta from '../hooks/usePageMeta';

const webappUrl =
  process.env.REACT_APP_PABSYNC_WEBAPP_URL || 'https://app.pabsync.com';
const signupUrl = `${webappUrl}/register?plan=free&src=website`;

const STATS = [
  { value: 'One', label: 'Platform for projects, tasks & automation' },
  { value: 'Human + AI', label: 'Collaboration built into the workflow' },
  { value: 'Self-hosted', label: 'AI inference — no per-seat AI cost' },
  { value: 'Minutes', label: 'From sign-up to your first project' },
];

const PRINCIPLES = [
  {
    icon: IconSpark,
    title: 'Clarity over clutter',
    desc: 'Every screen earns its place. We remove “work about work” so teams can focus on outcomes.',
  },
  {
    icon: IconCollab,
    title: 'Built for people and AI',
    desc: 'Structured context lets humans and AI helpers collaborate with the same source of truth.',
  },
  {
    icon: IconShield,
    title: 'Trustworthy by default',
    desc: 'Account-scoped access, granular roles, and full activity auditing keep your data yours.',
  },
];

const About: React.FC = () => {
  usePageMeta({
    title: 'About | PabSync',
    description:
      'PabSync is a work management platform for developers and AI collaboration — capture projects, organize tasks, and keep teams aligned.',
    path: '/about',
  });

  return (
    <div className="about">
      <main id="main">
        {/* Hero */}
        <section className="about-hero">
          <div className="container">
            <span className="eyebrow">
              <span className="eyebrow__dot" /> About PabSync
            </span>
            <h1>We’re in business to help your team thrive</h1>
            <p className="about-lede">
              PabSync is a work management platform for developers and AI
              collaboration. Capture projects, organize tasks, and keep teams
              aligned — with a workflow that stays simple as you scale.
            </p>
            <div className="about-hero-ctas">
              <a className="btn btn--primary btn--lg" href={signupUrl}>
                Start free
              </a>
              <a className="btn btn--ghost btn--lg" href="/contact">
                Talk to us
              </a>
            </div>

            <div className="about-stats">
              {STATS.map((s) => (
                <div className="about-stat" key={s.label}>
                  <b>{s.value}</b>
                  <span>{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Brand statement */}
        <section className="section section--alt">
          <div className="container about-brandblock">
            <Logo size={40} />
            <p>
              Our story is your story. We want you to do good work — motivated by
              mission and purpose — and have your work make a difference.
            </p>
          </div>
        </section>

        {/* Story rows */}
        <section className="section">
          <div className="container about-rows">
            <div className="about-row">
              <div className="about-row__media">
                <div className="about-media">
                  <span className="about-media__icon">
                    <IconCompass size={26} />
                  </span>
                  <div>
                    <div className="about-media__title">Built for clarity</div>
                    <div className="about-media__subtitle">
                      Less busywork. More progress.
                    </div>
                  </div>
                </div>
              </div>
              <div className="about-row__copy">
                <span className="about-row__eyebrow">How we started</span>
                <h2>Reduce the work about work</h2>
                <p>
                  PabSync started as a simple way for teams to plan work, track
                  tasks, and stay aligned in one place. From day one, the goal has
                  been to cut the meetings, status chasing, and scattered notes
                  that slow teams down.
                </p>
                <p>
                  We keep the product focused on what matters: clear ownership,
                  fast updates, and a workflow that works for both humans and AI
                  helpers — without adding complexity.
                </p>
              </div>
            </div>

            <div className="about-row about-row--reverse">
              <div className="about-row__media">
                <div className="about-media">
                  <span className="about-media__icon">
                    <IconTag size={26} />
                  </span>
                  <div>
                    <div className="about-media__title">
                      Keep everything in sync
                    </div>
                    <div className="about-media__subtitle">
                      People, projects, and progress.
                    </div>
                  </div>
                </div>
              </div>
              <div className="about-row__copy">
                <span className="about-row__eyebrow">Why “PabSync”</span>
                <h2>Synchronized on what matters</h2>
                <p>
                  The name reflects what the product is designed to do: keep teams
                  synchronized on priorities, execution, and outcomes.
                </p>
                <p>
                  Whether you’re an individual or an organization, the experience
                  should stay simple, compact, and professional.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Principles */}
        <section className="section section--alt">
          <div className="container">
            <div className="section__head">
              <span className="eyebrow eyebrow--muted">What we believe</span>
              <h2>Principles that shape the product</h2>
            </div>
            <div className="about-principles">
              {PRINCIPLES.map((p) => (
                <article className="card" key={p.title}>
                  <span className="card__icon">
                    <p.icon size={22} />
                  </span>
                  <h3>{p.title}</h3>
                  <p>{p.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section">
          <div className="container">
            <div className="about-cta">
              <span className="eyebrow eyebrow--muted" style={{ justifyContent: 'center' }}>
                <IconBolt size={16} /> Get started
              </span>
              <h2>Ready to keep your team in sync?</h2>
              <p>
                Create a free account in under a minute, or reach out to talk
                about rolling PabSync out across your organization.
              </p>
              <div className="about-cta-actions">
                <a className="btn btn--primary btn--lg" href={signupUrl}>
                  Start free
                </a>
                <a className="btn btn--ghost btn--lg" href="/pricing">
                  View pricing
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default About;
